import {Component, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Sort} from '@angular/material/sort';
import {Subject} from 'rxjs';
import {finalize, take, takeUntil} from 'rxjs/operators';
import {MatTableDataSource} from '@angular/material/table';
import {MAT_DIALOG_DATA, MatDialog} from '@angular/material/dialog';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {BasicPersonDetailDTO} from '../../../../ent/models/person/BasicPersonDetailDTO';
import {SharedService} from '../../../services/shared-service';
import {DateUtil} from '../../../util/DateUtil';
import {TableControl} from '../../../models/TableControl';
import {PagedFilterDTO} from '../../../models/PagedFilterDTO';
import {PagedResponseDTO} from '../../../models/PagedResponseDTO';
import {ClmRequestBuilder} from '../../../../clm/services/clm-request-builder';
import {detailsExpand} from '../../../animations';
import {SmsService} from '../../../../pbm/services/sms.service';
import {SmsDTO} from '../../../../pbm/models/sms/SmsDTO';
import {PagedRequestDTO} from '../../../models/PagedRequestDTO';
import {PbmRequestBuilder} from '../../../../pbm/services/pbm-request-builder';
import {FormControl} from '@angular/forms';

enum TableColumns {
  Icons = 'icons',
  SmsId = 'smsId',
  IsSent = 'isSent',
  ToNumber = 'toNumber',
  Message = 'message',
  CommunicationTriggerType = 'communicationTriggerType',
  TriggerId = 'triggerId',
  SmsTemplate = 'smsTemplate',
}

@Component({
  selector: 'app-sms-overview',
  templateUrl: './sms-overview.component.html',
  styleUrls: [
    '../../../../../../styles/global/_management-styles.scss',
    '../../../../../../styles/global/_dialog-styles.scss',
  ],
  animations: [detailsExpand]
})
export class SmsOverviewComponent implements OnInit, OnDestroy {
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly dateFormat = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly controls = ClmRequestBuilder.getTableControls();
  readonly tableColumns = TableColumns;

  isEditMode: boolean;
  loggedInUserName: string;
  smsOverrideFormControl = new FormControl('');
  smsTableControl: TableControl;
  smsExpandedRow: SmsDTO | null;
  smsDisplayedColumns: string[] = Object.values(TableColumns);
  smsDataSource: MatTableDataSource<SmsDTO> = new MatTableDataSource();

  @ViewChild('smsPaginator', {static: false}) smsPaginator: MatPaginator;

  constructor(
    private smsService: SmsService,
    public dialog: MatDialog,
    private sharedService: SharedService,
    @Inject(MAT_DIALOG_DATA) public basicPersonDetailDTO: BasicPersonDetailDTO) {
    this.smsTableControl = new TableControl(
      this.controls.length,
      this.controls.pageSize,
      this.controls.pageNumber,
      this.controls.pageSizeOptions,
      'smsId',
      'desc',
      '',
      '');
  }


  get isDataExist(): boolean {
    return !!(this.smsDataSource && this.smsDataSource.data?.length);
  }

  ngOnInit(): void {
    this.loggedInUserName = this.sharedService.getLoggedInUserName();
    this.getListOfSMS(this.smsTableControl);
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  initSubscriptions(): void {
    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    if (this.smsPaginator) {
      this.smsPaginator.firstPage();
      this.smsTableControl.pageNumber = 0;
    }

    if ($event.active === TableColumns.CommunicationTriggerType) {
      this.smsTableControl.sortField = 'communicationTriggerTypeByCommunicationTriggerTypeId.name';
    }
    else if ($event.active === TableColumns.SmsTemplate) {
      this.smsTableControl.sortField = 'smsTemplateBySmsTemplateId.processingClassByProcessingClassId.className';
    }
    else {
      this.smsTableControl.sortField = $event.active;
    }
    this.smsTableControl.sortDirection = $event.direction;
    this.getListOfSMS(this.smsTableControl);
  }

  pageChanged($event: PageEvent): void {
    this.smsTableControl.pageNumber = $event.pageIndex;
    this.smsTableControl.pageSize = $event.pageSize;
    this.getListOfSMS(this.smsTableControl);
  }

  onRowClick(event: MouseEvent, row): void {
    const targetElement = event.target as HTMLElement;
    if (targetElement.classList.contains('mat-checkbox-inner-container')) {
      event.preventDefault();
      event.stopPropagation();
      row.sendSmsChecked = !row.sendSmsChecked;
    }
    else {
      this.smsExpandedRow = this.smsExpandedRow === row ? null : row;
    }
  }

  onClickSendSmsConfirmed(event: MouseEvent, row: SmsDTO): void {
    event.preventDefault();
    event.stopPropagation();

    let smsDTO: SmsDTO;
    if (row.smsId !== null) {
      // Check to avoid updated/updated_by audit fields fo duplicate case
      // as we are already handling logic inside OnClickDuplicate.
      smsDTO = this.getSmsDTOPayload(row);
    }
    else {
      smsDTO = row;
    }

    if (row.editedToNumber) {
      smsDTO.toNumber = row.editedToNumber;
    }
    // Front-end sent emails must override the toAddress always
    smsDTO.toNumberManualOverride = true;

    row.smsSentBusyCounter++;
    this.smsService.sendSms(smsDTO)
      .pipe(
        takeUntil(this.destroyed$),
        finalize(() => {
          row.smsSentBusyCounter--;
          this.onClickToggleIcons(event, row);
        })
      )
      .subscribe(
        (_) => {
          this.sharedService.openSnackBar('SMS sent successfully!', 'Dismiss');
          this.getListOfSMS(this.smsTableControl);
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        });
  }

  onClickToggleIcons(event: MouseEvent, row: SmsDTO): void {
    event.preventDefault();
    event.stopPropagation();
    row.sendSmsChecked = !row.sendSmsChecked;
  }

  onClickEditNumber($event: MouseEvent, row: SmsDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    row.isEditNumber = true;
    const value: string = row?.editedToNumber ? row.editedToNumber : row.toNumber;
    this.smsOverrideFormControl.patchValue(value);
  }

  onClickCancelEditNumber($event: MouseEvent, row: SmsDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    row.isEditNumber = false;
    row.editedToNumber = '';
    this.smsOverrideFormControl.patchValue('');
    this.smsOverrideFormControl.updateValueAndValidity();
  }

  onClickSaveNumber($event: MouseEvent, row: SmsDTO): void {
    $event.preventDefault();
    $event.stopPropagation();

    if (!this.smsOverrideFormControl.value) {
      this.sharedService.openSnackBar('Provider number value', 'Dismiss');
      return;
    }

    row.editedToNumber = this.smsOverrideFormControl.value;
    row.isEditNumber = false; // close edit mode
  }

  OnClickDuplicate(duplicate: boolean, row: SmsDTO): void {
    if (duplicate) {
      let newSMS: SmsDTO = this.getSmsDTOPayload(row);
      newSMS = {
        ...newSMS,
        updated: null,
        updatedBy: null,
        created:  new Date(),
        createdBy: this.loggedInUserName,
        smsId: null,
        isSent: 0, // This will enable send/edit icons
      };
      const updatedData: SmsDTO[] = this.smsDataSource.data.slice();
      updatedData.unshift(newSMS);
      this.smsDataSource.data = updatedData;
    }
  }

  getToNumber(row: SmsDTO): string {
    if (row.editedToNumber) {
      return row.editedToNumber;
    }
    return row.toNumber;
  }

  private getSmsDTOPayload(smsDTO: SmsDTO): SmsDTO {
    let payload: SmsDTO;
    payload = {
      ...smsDTO,
      updated: new Date(),
      updatedBy: this.loggedInUserName
    } as SmsDTO;

    return payload;
  }

  private getListOfSMS(tableControl: TableControl): void {
    const pagedRequestDTO: PagedRequestDTO = PbmRequestBuilder.getListOfSMSPagedRequestDTO(tableControl);
    pagedRequestDTO.fieldMap = {};

    const personPagedFilterDTO: PagedFilterDTO = new PagedFilterDTO();
    personPagedFilterDTO.name = 'insuredPersonByInsuredPersonId.insuredPersonId';
    personPagedFilterDTO.value = String(this.basicPersonDetailDTO.personId);
    pagedRequestDTO.fieldMap[`insuredPersonByInsuredPersonId`] = personPagedFilterDTO;

    this.smsService.getListOfSMS(pagedRequestDTO)
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe(
        (response: PagedResponseDTO<SmsDTO>) => {
          response.resultList.forEach((item: SmsDTO): void => {
            item.sendSmsChecked = false;
            item.smsSentBusyCounter = 0;
          });
          this.smsDataSource.data = response.resultList;
          this.smsTableControl.length = response.totalRecords;
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openSnackBar(error.businessMessage, 'Dismiss');
        }
      );
  }
}
